import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, Button, Alert, Spinner } from 'react-bootstrap';
import './TicketChat.scss';

const TicketChat = ({ ticketId, onBack, ticketInfo }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
              const response = await fetch(`/api/trengo/tickets/${ticketId}/messages`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setMessages(data.data || data);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError(`Failed to fetch messages: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (ticketId) {
      fetchMessages();
    }
  }, [ticketId, fetchMessages]);

  const getMessageTypeColor = (type, messageType) => {
    // Use message_type from backend if available
    if (messageType) {
      switch (messageType) {
        case 'CUSTOMER':
          return 'success';
        case 'AGENT':
          return 'primary';
        default:
          return 'secondary';
      }
    }
    
    // Fallback to old logic
    switch (type) {
      case 'INBOUND':
        return 'success';
      case 'OUTBOUND':
        return 'primary';
      case 'NOTE':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getSenderName = (message) => {
    // Use the sender_name from backend if available
    if (message.sender_name) {
      return message.sender_name;
    }
    
    // Fallback to old logic if sender_name is not available
    if (message.type === 'INBOUND') {
      return 'Customer';
    } else if (message.type === 'OUTBOUND') {
      return 'Agent';
    } else if (message.type === 'NOTE') {
      return 'System Note';
    }
    return 'Unknown';
  };

  if (!ticketId) {
    return null;
  }

  return (
    <div className="ticket-chat-container">
      <Card className="h-100">
        <Card.Header className="d-flex justify-content-between align-items-center bg-primary text-white">
          <div>
            <h5 className="mb-0">💬 Ticket #{ticketInfo?.trengo_id || ticketId}</h5>
            <small>
              {ticketInfo?.subject || 'No subject'} - 
              <Badge 
                bg={
                  ticketInfo?.status === 'OPEN' ? 'success' :
                  ticketInfo?.status === 'CLOSED' ? 'secondary' :
                  ticketInfo?.status === 'ASSIGNED' ? 'primary' :
                  'warning'
                }
                className="ms-2"
              >
                {ticketInfo?.status || 'UNKNOWN'}
              </Badge>
              {ticketInfo?.channel && (
                <Badge bg="info" className="ms-2">
                  {ticketInfo.channel.display_name || ticketInfo.channel.name}
                </Badge>
              )}
            </small>
          </div>
          <Button 
            variant="outline-light" 
            size="sm"
            onClick={onBack}
          >
            ← Back
          </Button>
        </Card.Header>
        
        <Card.Body className="p-0 d-flex flex-column" style={{ height: 'calc(100vh - 200px)' }}>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center flex-grow-1">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : error ? (
            <Alert variant="danger" className="m-3">
              {error}
            </Alert>
          ) : (
            <>
              {/* Messages Container */}
              <div className="messages-container flex-grow-1 p-3" style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
                {messages.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <p>No messages found for this ticket.</p>
                  </div>
                ) : (
                  <div className="messages-list">
                    {messages.map((message) => (
                      <div key={message.id} className="message-item mb-3">
                        <div className="d-flex align-items-start">
                          <div className="message-avatar me-3">
                            <div className={`avatar-circle bg-${getMessageTypeColor(message.type, message.message_type)}`}>
                              {getSenderName(message).charAt(0)}
                            </div>
                          </div>
                          <div className="message-content flex-grow-1">
                            <div className="message-header d-flex justify-content-between align-items-center mb-1">
                              <div>
                                <strong className="me-2">{getSenderName(message)}</strong>
                                <Badge bg={getMessageTypeColor(message.type, message.message_type)} size="sm">
                                  {message.message_type || message.type || 'Unknown'}
                                </Badge>
                              </div>
                              <small className="text-muted">
                                {formatTimestamp(message.created_at)}
                              </small>
                            </div>
                            <div className="message-body">
                              {message.content ? (
                                <div className="message-text p-2 rounded" style={{ 
                                  backgroundColor: (message.message_type === 'CUSTOMER' || message.type === 'INBOUND') ? '#f8f9fa' : '#e3f2fd',
                                  border: '1px solid #dee2e6'
                                }}>
                                  {message.content.split('\n').map((line, index) => (
                                    <div key={index}>
                                      {line}
                                      {index < message.content.split('\n').length - 1 && <br />}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-muted">
                                  <em>No content</em>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="chat-footer p-3 border-top bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    {messages.length} message{messages.length !== 1 ? 's' : ''}
                  </small>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={fetchMessages}
                    disabled={loading}
                  >
                    {loading ? '🔄' : '🔄'} Refresh
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default TicketChat; 